import { AcFormProvider, AcStepIndicator, AcTitle, AcTypography } from '@components'
import { LABELS } from '@constants'
import { SandwichSubscriptionForm } from '@partials/forms/sandwich-subscription/sandwich-subscription'
import { ISandwichSubscriptionPayload } from '@typings'
import { PublicFormsApi } from 'src/api/public-forms.api'
import { useWidgetContext } from 'src/widget/widget-context'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import styles from './ac-sandwich-subscription.module.scss'

const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
] as const

const STEP_LABELS = [
  LABELS.STEP_DELIVERY_DAYS,
  LABELS.STEP_COMPANY_INFO,
  LABELS.STEP_CONTACT_INFO,
  LABELS.STEP_ADDRESS_INFO,
  LABELS.STEP_SUBMIT,
]

const TOTAL_STEPS = STEP_LABELS.length

const STEP_FIELDS: Record<number, (keyof ISandwichSubscriptionPayload)[]> = {
  1: ['start_date', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  2: ['company_name', 'invoice_company', 'invoice_email'],
  3: ['first_name', 'last_name', 'email', 'phone'],
  4: [
    'street',
    'house_number',
    'house_number_addition',
    'postal_code',
    'city',
  ],
  5: ['tac'],
}

const api = new PublicFormsApi()

export const AcSandwichSubscription = () => {
  const widgetCtx = useWidgetContext()

  const hutk = useRef((() => {
    const cookieMatch = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]*)/)
    if (cookieMatch) return cookieMatch[1]
    return new URLSearchParams(window.location.search).get('hutk')
  })())

  useEffect(() => {
    // In widget mode, background is handled by :host CSS
    if (widgetCtx) return

    const app = document.getElementById('app')
    if (app) {
      app.style.background = '#fcfaee'
      return () => {
        app.style.background = ''
      }
    }
  }, [])

  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(false)
  const [success, setSuccess] = useState(false)
  const [daysError, setDaysError] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState<number[]>([])

  const form = useForm<ISandwichSubscriptionPayload>({
    defaultValues: {
      company_name: '',
      invoice_company: null,
      invoice_email: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      street: '',
      house_number: null,
      house_number_addition: null,
      postal_code: '',
      city: '',
      start_date: '',
      monday: null,
      tuesday: null,
      wednesday: null,
      thursday: null,
      friday: null,
      tac: false,
    },
  })

  const hasAtLeastOneDay = (data: ISandwichSubscriptionPayload) => {
    return WEEKDAY_KEYS.some(
      day => data[day] !== null && data[day] !== undefined
    )
  }

  const handleErrors = (errors: Record<string, string[]>) => {
    const errorKeys = Object.keys(errors)
    errorKeys.forEach(key =>
      form.setError(key as keyof ISandwichSubscriptionPayload, {
        type: 'custom',
        message: errors[key][0],
      })
    )
  }

  const handleNext = useCallback(async () => {
    setDaysError(null)

    // Step 1 custom validation: at least one day selected
    if (currentStep === 1) {
      const values = form.getValues()
      if (!hasAtLeastOneDay(values)) {
        setDaysError(LABELS.SELECT_AT_LEAST_ONE_DAY)
        return
      }
    }

    // Client-side validation for current step fields
    const fields = STEP_FIELDS[currentStep]
    const isValid = await form.trigger(fields)
    if (!isValid) return

    // Precognitive server-side validation
    setValidating(true)
    try {
      const data = form.getValues()
      await api.validate_sandwich_subscription_step(
        fields as string[],
        data
      )

      // Mark step as completed and advance
      setCompletedSteps(prev =>
        prev.includes(currentStep) ? prev : [...prev, currentStep]
      )
      setCurrentStep(prev => prev + 1)
    } catch (e: any) {
      if (e?.response?.status === 422 && e?.response?.data?.errors) {
        handleErrors(e.response.data.errors)
      } else {
        toast.error(e?.response?.data?.message || LABELS.SERVER_ERROR)
      }
    } finally {
      setValidating(false)
    }
  }, [currentStep, form])

  const handleBack = useCallback(() => {
    setCurrentStep(prev => prev - 1)
  }, [])

  const onSubmit = async (data: FieldValues) => {
    setDaysError(null)

    const payload = {
      ...data,
      hutk: hutk.current,
    } as ISandwichSubscriptionPayload
    if (!hasAtLeastOneDay(payload)) {
      setDaysError(LABELS.SELECT_AT_LEAST_ONE_DAY)
      setCurrentStep(1)
      return
    }

    setLoading(true)
    try {
      await api.submit_sandwich_subscription(payload)
      setSuccess(true)
    } catch (e: any) {
      if (e?.response?.status === 422 && e?.response?.data?.errors) {
        handleErrors(e.response.data.errors)
      } else {
        toast.error(e?.response?.data?.message || LABELS.SERVER_ERROR)
      }
    } finally {
      setLoading(false)
    }
  }

  const onFormSubmit = () => {
    form.handleSubmit(onSubmit)()
  }

  const renderSuccess = useMemo(
    () => (
      <div className={styles['ac-sandwich-subscription__success']}>
        <AcTitle level={2}>
          {LABELS.SANDWICH_SUBSCRIPTION_SUCCESS_TITLE}
        </AcTitle>
        <AcTypography>{LABELS.SANDWICH_SUBSCRIPTION_SUCCESS_TEXT}</AcTypography>
      </div>
    ),
    []
  )

  if (success) {
    return (
      <div className={styles['ac-sandwich-subscription']}>{renderSuccess}</div>
    )
  }

  return (
    <div className={styles['ac-sandwich-subscription']}>
      <div className={styles['ac-sandwich-subscription__header']}>
        <AcTitle level={1}>Bestelformulier</AcTitle>
        <AcTypography>Bestel je belegde broodjes abonnement</AcTypography>
      </div>
      <AcStepIndicator
        steps={STEP_LABELS}
        currentStep={currentStep}
        completedSteps={completedSteps}
      />
      <AcFormProvider
        form={form}
        onSubmit={onFormSubmit}
        loading={loading}>
        <SandwichSubscriptionForm
          onSubmit={onFormSubmit}
          loading={loading}
          daysError={daysError}
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
          onNext={handleNext}
          onBack={handleBack}
          validating={validating}
        />
      </AcFormProvider>
    </div>
  )
}
